// src/Components/Event/EventButton.tsx
import { PlusIcon, WrenchIcon } from "lucide-react";
import {
  CreateEventModal,
  NOT_VALID_COMPETITION_ID,
} from "./Modals/createEventModal";
import AdminToolbar from "../Admin/AdminToolbar";
import {
  BreadcrumbLink,
  Flex,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Heading,
  Image,
  Spinner,
  Text,
  VStack,
  Stack,
  Show,
  Spacer,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { format } from "../../format";
import { useState } from "react";

import { useAwaitResponse } from "../../Api/HttpRequestHelpers";
import { loggoResolver } from "../../loggos/LoggoSelector";
import { Competition_GET } from "../../Api/dto/Competition_GET";
import { CompetitionClient } from "../../Api/client/CompetitionClient";
import { Role, useSessionState } from "../../Session/SessionService";
import { Competition_POST_PUT } from "../../Api/dto/Competition_POST_PUT";

export const Events = () => {
  const [enableCompetitionEditing, setEditEvent] = useState<boolean>(false);
  const [eventToEdit, setEventToEdit] = useState<undefined | Competition_GET>(
    undefined
  );
  const session = useSessionState();
  const [showCreateEventModal, setShowCreateEventModal] =
    useState<boolean>(false);

  const [competitions, isLoadingCompetitions, setCompetitions] =
    useAwaitResponse(
      [] as Array<Competition_GET>,
      () => {
        if (session.hasUserMode(Role.ADMIN)) {
          return CompetitionClient.admin.getAllCompetitions();
        } else {
          return CompetitionClient.getCompetitions();
        }
      },
      (response) => response.body ?? []
    );

  if (isLoadingCompetitions) {
    return <Spinner />;
  }
  console.log(session, session.hasUserMode(Role.ADMIN), session.role);

  return (
    <>
      <Breadcrumb mb={8}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="..">
            Start
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Tävlingar</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <AdminToolbar
        onOpen={() => setEditEvent(true)}
        onClose={() => setEditEvent(false)}
        hidden={!session.hasUserMode(Role.ADMIN)}
      >
        <Button
          background={"red"}
          leftIcon={<PlusIcon />}
          onClick={() => {
            setShowCreateEventModal(true);
          }}
        >
          Skapa ny tävling
        </Button>
      </AdminToolbar>
      {(showCreateEventModal || eventToEdit) && (
        <CreateEventModal
          competitionToEdit={eventToEdit}
          onSave={(competition: Competition_GET) => {
            setShowCreateEventModal(false);
            setEventToEdit(undefined);
            const { id, ...postPutBody } = competition;
            if (id !== NOT_VALID_COMPETITION_ID) {
              CompetitionClient.admin.putCompetition(id, postPutBody);
              setCompetitions((old) => {
                return [...old.filter((c) => c.id !== id), competition];
              });
            } else {
              CompetitionClient.admin.postCompetition(postPutBody);
            }
          }}
          onClose={() => {
            setShowCreateEventModal(false);
            setEventToEdit(undefined);
          }}
        />
      )}
      <CurrentCompetitions
        competitions={competitions.filter(
          (c) => enableCompetitionEditing || !c.deleted
        )}
        enableCompetitionEditing={enableCompetitionEditing}
        onEdit={(eventTag: string) => {
          const selectedCompetitions = competitions.filter(
            (c) => c.tag === eventTag
          );
          if (selectedCompetitions.length > 0) {
            setEventToEdit(selectedCompetitions[0]);
          }
        }}
      />
      <OurGdprPolicy />
    </>
  );
};

function CurrentCompetitions(props: {
  competitions: Competition_GET[];
  enableCompetitionEditing: boolean;
  onEdit: (competitionTag: string) => void;
}) {
  if (props.competitions == undefined || props.competitions.length === 0) {
    return <div>Inga tävlingar hittades.</div>;
  }

  return (
    <>
      <Heading size="lg" marginBottom={"30px"}>
        Aktuella tävlingar
      </Heading>
      {props.competitions.map((event) => {
        return (
          <EventButton
            key={event.id}
            eventTag={event.tag ?? ""}
            eventName={event.name ?? "Event"}
            startDate={event.startTime ?? 0}
            imageSrc={event.imageSrc}
            backgroundColor={event.deleted ? "#ffcfd5" : "#cfefff"}
            editEvent={props.enableCompetitionEditing}
            onEdit={props.onEdit}
          />
        );
      })}
    </>
  );
}

interface EventButtonProps {
  eventTag: string;
  eventName: string;
  startDate: number;
  imageSrc?: string | null; // Optional image source
  backgroundColor: string;
  editEvent: boolean;
  onEdit: (eventTag: string) => void;
}

const EventButton: React.FC<EventButtonProps> = ({
  eventTag,
  eventName,
  startDate,
  imageSrc,
  backgroundColor,
  editEvent,
  onEdit,
}) => {
  const isSmallScreen = useBreakpointValue({
    base: true,
    sm: false,
  });
  return (
    <VStack
      borderRadius="8px"
      background={editEvent ? "pink" : undefined}
      align={"left"}
      _hover={{
        transform: "scale(1.05)",
        transition: "transform 0.3s ease-in-out",
      }}
    >
      <Link
        to={eventTag + ""}
        key={eventTag}
        className="w-full"
        style={{ width: "100%", height: "100%" }}
      >
        <Box
          width="100%"
          padding="10px"
          border="1px solid #ccc"
          marginBottom="10px"
          borderRadius="8px"
          boxShadow="0 4px 8px rgba(0, 0, 0, 0.1)"
          backgroundColor={backgroundColor}
        >
          <Stack direction={"row"}>
            <VStack>
              <Box>
                <Text fontSize="24px" fontWeight="bold">
                  {eventName}
                </Text>
                <Show below={"md"}>
                  <Text fontSize="24px" color="#555">
                    {format(new Date(startDate), "PPP")}
                  </Text>
                </Show>
                <Show above="md">
                  <Text fontSize="24px" color="#555">
                    {format(new Date(startDate), "PPPP")}
                  </Text>
                </Show>
              </Box>
              {imageSrc && isSmallScreen && (
                <Image
                  src={loggoResolver(imageSrc)}
                  alt={""}
                  maxWidth="90%"
                  height="auto"
                  maxHeight={"200px"}
                  marginRight="10px"
                />
              )}
            </VStack>
            <Spacer />
            {imageSrc && !isSmallScreen && (
              <Image
                src={loggoResolver(imageSrc)}
                alt={""}
                maxWidth="50%"
                height="auto"
                maxHeight={"200px"}
                marginRight="10px"
              />
            )}
          </Stack>
        </Box>
      </Link>
      <Button
        margin={"20px"}
        marginTop={"0px"}
        leftIcon={<WrenchIcon />}
        alignSelf={"flex-end"}
        width={"fit-content"}
        visibility={editEvent ? "visible" : "collapse"}
        onClick={() => onEdit(eventTag)}
      >
        Ändra
      </Button>
    </VStack>
  );
};

const OurGdprPolicy = () => {
  return (
    <Box
      marginTop={"180px"}
      width={"80%"}
      key={"GDPR"}
      shadow={"2xl"}
      border="1px"
      borderColor="gray.300"
      background={"gray.200"}
      p={4}
      rounded="xl"
    >
      <Heading size="md">Vår hantering av dina personuppgifter</Heading>
      <Text>
        För att kunna bedriva vår verksamhet så måste vi samla in och spara viss
        personlig information om våra deltagare. De kontaktuppgifter du fyller i
        när du anmäler dig på våra tävlingar används endast ifall vi behöver
        kontakta dig angående sagd tävling. Efter att tävlingen avslutats och
        eventuella betalningar är gjorda tar vi därför bort den information som
        vi inte längre behöver. Viss information, så som namn, ålder och
        resultat, kan dock komma att sparas för att kunna skapa resultatlistor
        och tävlingsstatistik.
      </Text>
      <Text>
        Bilder som tas under våra evenemang använder vi ibland för att
        marknadsföra vår lokala verksamhet på vår hemsida, facebooksida och vårt
        instagramkonto. Det går bra att kontakta oss om du vill att vi tar bort
        bilder från våra sociala medier.
      </Text>
      <Text>
        Genom att delta på våra tävlingar godkänner du vår hantering av
        personuppgifter och bilder. Om du har ytterligare funderingar angående
        hur vi hanterar dina uppgifter så är du välkommer att höra av dig.
      </Text>
    </Box>
  );
};
